import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import type { OzilGoalQualityId } from "./ozil-goal-preset";

/**
 * /ozil-goal-ai-video-generator 样片区和右栏 Examples 的片子，做法同 bulin-47-showcase.ts。
 *
 * Examples 优先用竞品样片（2026-10-23 抓取）：mitte.ai 一条、aireel.net 两条，无水印、带原声 Clandestina，
 * onSite false，不进 VideoObject，卡上不标来源和模型。自产片在本页工作台用预设出，onSite true，
 * 「Try this example」回填原图。来源和压制参数见 packages/scripts/ozil-goal-showcase/README.md。
 *
 * R2 `home/showcase/ozil-goal/` 下 clips/{id}.mp4（拼贴小文件）、clips/{id}-720p.mp4（右栏大预览）、
 * posters/{id}-720p.jpg、refs/{ref}.jpg。清单为空时样片区整个不渲染。
 */
export interface OzilGoalShowcaseClip {
	/** 标题和一句话说明在 ozilGoal.showcase.items.{id}，JSON-LD 与 <video> aria-label 也用它 */
	id: string;
	/** 出这条片用的画质档（只有自产片有） */
	quality?: OzilGoalQualityId;
	orientation: "landscape" | "portrait";
	/** 站内生成的才 true；false 的不进 VideoObject，文案也不能说是本站出的 */
	onSite: boolean;
	duration: number;
	videoUrl: string;
	/** 右栏 Examples 大预览用的大文件（不超过 720 宽，原片更小就是原分辨率）；拼贴 tile 用 videoUrl 的小文件 */
	previewVideoUrl: string;
	poster: string;
	/** 成片带音轨：拼贴右上角出静音开关 */
	hasAudio: boolean;
	/** 自产片的原图，「Try this example」填进照片槽；外部片没有原图，不出这个按钮 */
	inputImages: string[];
	/** 外部片的来源（只记档案，页面上不显示） */
	source?: string;
}

const R2_BASE = "home/showcase/ozil-goal";

/**
 * 视频和 poster 地址的版本参数。assets 域的 GET 走 Cloudflare 边缘缓存（max-age 一天），同一个 key
 * 覆盖上传后一天内还是旧文件，上传前被请求过的连 404 也会缓存一天；换片子时把这个数字加一
 */
const CLIP_VERSION = 1;

type Spec = Omit<
	OzilGoalShowcaseClip,
	"videoUrl" | "previewVideoUrl" | "poster" | "inputImages"
> & {
	/** 自产片的原图（refs/{ref}.jpg） */
	ref?: string;
};

/**
 * 顺序即拼贴和右栏 Examples 的顺序，自产片排第 1、3、5 条（同 Bulin 47）。自产片是本页 HD（Seedance 2.0 Mini
 * 720p，15 秒）2026-10-24 出的，服务端已套原声；外部片 2026-10-23 抓取
 */
const SPECS: Spec[] = [
	{
		id: "hd-grandma",
		quality: "hd",
		orientation: "portrait",
		onSite: true,
		duration: 15,
		hasAudio: true,
		ref: "grandma-1",
	},
	{
		id: "mitte-black-shirt",
		orientation: "portrait",
		onSite: false,
		duration: 15,
		hasAudio: true,
		source: "https://mitte.ai/app/ozil-goal-ai",
	},
	{
		id: "hd-suit",
		quality: "hd",
		orientation: "portrait",
		onSite: true,
		duration: 15,
		hasAudio: true,
		ref: "suit-man-1",
	},
	{
		id: "aireel-gold-jacket",
		orientation: "portrait",
		onSite: false,
		duration: 15,
		hasAudio: true,
		source: "https://www.aireel.net/ai-effects/ai-ozil-goal-trend",
	},
	{
		id: "hd-woman",
		quality: "hd",
		orientation: "portrait",
		onSite: true,
		duration: 15,
		hasAudio: true,
		ref: "woman-1",
	},
	{
		id: "aireel-brown-top",
		orientation: "portrait",
		onSite: false,
		duration: 15,
		hasAudio: true,
		source: "https://www.aireel.net/ai-effects/ai-ozil-goal-trend",
	},
];

export const ozilGoalShowcaseClips: OzilGoalShowcaseClip[] = SPECS.map(
	({ ref, ...spec }) => ({
		...spec,
		videoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}.mp4?v=${CLIP_VERSION}`,
		),
		previewVideoUrl: buildR2AssetsImageUrl(
			`${R2_BASE}/clips/${spec.id}-720p.mp4?v=${CLIP_VERSION}`,
		),
		poster: buildR2AssetsImageUrl(`${R2_BASE}/posters/${spec.id}-720p.jpg`),
		inputImages: ref
			? [buildR2AssetsImageUrl(`${R2_BASE}/refs/${ref}.jpg`)]
			: [],
	}),
);

/** 样片上传日期，喂 VideoObject.uploadDate（完整 ISO 8601 带时区） */
export const OZIL_GOAL_SHOWCASE_UPLOADED_AT = "2026-10-24T00:00:00+08:00";
